import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Button, Form, Modal, Badge, Spinner, ButtonGroup, Alert } from 'react-bootstrap';
import { FaFileContract, FaEye, FaSearch, FaPlus, FaEdit, FaTrash, FaCalendarAlt } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import api from '../services/api';
import DeleteConfirmModal from '../components/DeleteConfirmModal';

import ExcelExportButton from '../components/ExcelExportButton';


const StandaloneSenetListPage = () => {
    const [senets, setSenets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [selectedSenet, setSelectedSenet] = useState(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);


    // Filters
    const [filters, setFilters] = useState({
        search: '',
        dateFrom: '',
        dateTo: ''
    });

    // Pagination
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 20,
        total: 0,
        pages: 0
    });

    // Fetch senets
    const fetchSenets = async () => {
        try {
            setLoading(true);
            const response = await api.get('/reports/standalone-senets', {
                params: {
                    ...filters,
                    page: pagination.page,
                    limit: pagination.limit
                }
            });

            setSenets(response.data.reports);
            setPagination(response.data.pagination);
        } catch (error) {
            console.error('Senet listeleme hatası:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchSenets();
    }, [filters, pagination.page]);

    // Handle search
    const handleSearch = (e) => {
        e.preventDefault();
        setPagination(prev => ({ ...prev, page: 1 }));
        fetchSenets();
    };

    // Clear filters
    const clearFilters = () => {
        setFilters({
            search: '',
            dateFrom: '',
            dateTo: ''
        });
        setPagination(prev => ({ ...prev, page: 1 }));
    };

    // Show senet
    const handleShowSenet = (senetId) => {
        const token = localStorage.getItem('token');
        const senetUrl = `${process.env.REACT_APP_API_URL}/reports/${senetId}/senet?token=${token}`;
        window.open(senetUrl, '_blank', 'noopener,noreferrer');
    };

    // Show details
    const handleShowDetails = async (senetId) => {
        try {
            const response = await api.get(`/reports/${senetId}`);
            setSelectedSenet(response.data.report);
            setShowDetailModal(true);
        } catch (error) {
            console.error('Senet detay hatası:', error);
        }
    };

    // Format currency
    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('tr-TR', {
            style: 'currency',
            currency: 'TRY',
            minimumFractionDigits: 0
        }).format(amount);
    };

    // Delete senet
    const handleDeleteSenet = async (senetId) => {
        try {
            await api.delete(`/reports/${senetId}`);
            // Listeyi yeniden yükle
            fetchSenets();
            alert('Senet başarıyla silindi');
        } catch (error) {
            console.error('Senet silme hatası:', error);
            throw error; // Modal'da error handling için
        }
    };

    const handleShowDeleteModal = (senet) => {
        setSelectedSenet(senet);
        setShowDeleteModal(true);
    };

    // Calculate payment status
    const getPaymentStatus = (paymentPlan) => {
        if (!paymentPlan || paymentPlan.length === 0) return { paid: 0, pending: 0, total: 0 };

        const paid = paymentPlan.filter(item => item.paymentStatus === 'paid').length;
        const pending = paymentPlan.filter(item => item.paymentStatus === 'pending').length;
        const total = paymentPlan.length;

        return { paid, pending, total };
    };

    // Get status badge
    const getStatusBadge = (paymentPlan) => {
        const status = getPaymentStatus(paymentPlan);

        if (status.paid === 0) {
            return <Badge bg="warning">Başlamadı</Badge>;
        } else if (status.paid === status.total) {
            return <Badge bg="success">Tamamlandı</Badge>;
        } else {
            return <Badge bg="info">{status.paid}/{status.total} Ödendi</Badge>;
        }
    };

    // Pagination component
    const renderPagination = () => {
        if (pagination.pages <= 1) return null;

        const pages = [];
        for (let i = 1; i <= Math.min(pagination.pages, 10); i++) {
            pages.push(
                <Button
                    key={i}
                    variant={i === pagination.page ? 'primary' : 'outline-primary'}
                    size="sm"
                    onClick={() => setPagination(prev => ({ ...prev, page: i }))}
                    className="me-1"
                >
                    {i}
                </Button>
            );
        }

        return (
            <div className="d-flex justify-content-center mt-3">
                <div>
                    <Button
                        variant="outline-primary"
                        size="sm"
                        disabled={pagination.page === 1}
                        onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                        className="me-2"
                    >
                        Önceki
                    </Button>
                    {pages}
                    <Button
                        variant="outline-primary"
                        size="sm"
                        disabled={pagination.page === pagination.pages}
                        onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                        className="ms-2"
                    >
                        Sonraki
                    </Button>
                </div>
            </div>
        );
    };

    if (loading && senets.length === 0) {
        return (
            <Container className="py-5 text-center">
                <Spinner animation="border" role="status">
                    <span className="visually-hidden">Yükleniyor...</span>
                </Spinner>
            </Container>
        );
    }

    return (
        <Container className="py-5">
            <Row className="mb-4">
                <Col md={8}>
                    <h1>Sözleşmesiz Senetler</h1>
                    <p className="text-muted">Oluşturulan tüm sözleşmesiz senetleri görüntüleyin</p>
                </Col>
                <Col md={4} className="text-end">
                    <ButtonGroup>
                        <ExcelExportButton />
                        <Link to="/standalone-senet" className="btn btn-primary">
                            <FaPlus className="me-2" />
                            Yeni Senet Oluştur
                        </Link>
                    </ButtonGroup>
                </Col>
            </Row>

            {/* Stats */}
            <Row className="mb-4">
                <Col md={3}>
                    <Card className="text-center h-100">
                        <Card.Body>
                            <h3 className="text-primary">{pagination.total}</h3>
                            <p className="text-muted mb-0">Toplam Senet</p>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={3}>
                    <Card className="text-center h-100">
                        <Card.Body>
                            <h5 className="text-success">
                                {formatCurrency(senets.reduce((sum, senet) => sum + (senet.vehicleData?.price || 0), 0))}
                            </h5>
                            <p className="text-muted mb-0">Toplam Değer</p>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={3}>
                    <Card className="text-center h-100">
                        <Card.Body>
                            <h5 className="text-info">
                                {senets.filter(senet => {
                                    const status = getPaymentStatus(senet.paymentPlan);
                                    return status.paid > 0 && status.paid < status.total;
                                }).length}
                            </h5>
                            <p className="text-muted mb-0">Devam Eden</p>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={3}>
                    <Card className="text-center h-100">
                        <Card.Body>
                            <h5 className="text-success">
                                {senets.filter(senet => {
                                    const status = getPaymentStatus(senet.paymentPlan);
                                    return status.paid === status.total;
                                }).length}
                            </h5>
                            <p className="text-muted mb-0">Tamamlanan</p>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Senet List */}
            <Card>
                <Card.Body className="p-0">
                    {loading && (
                        <div className="text-center p-3">
                            <Spinner animation="border" size="sm" className="me-2" />
                            Yükleniyor...
                        </div>
                    )}

                    <div className="table-responsive">
                        <Table hover className="mb-0">
                            <thead>
                                <tr>
                                    <th>Tarih</th>
                                    <th>Müşteri</th>
                                    <th>Kimlik Türü</th>
                                    <th>Araç</th>
                                    <th>Tutar</th>
                                    <th>Taksit</th>
                                    <th>Durum</th>
                                    <th>İşlemler</th>
                                </tr>
                            </thead>
                            <tbody>
                                {senets.length > 0 ? (
                                    senets.map(senet => {
                                        const paymentStatus = getPaymentStatus(senet.paymentPlan);
                                        return (
                                            <tr key={senet._id}>
                                                <td>
                                                    <div>
                                                        <strong>{new Date(senet.createdAt).toLocaleDateString('tr-TR')}</strong>
                                                        <br />
                                                        <small className="text-muted">
                                                            <FaCalendarAlt className="me-1" />
                                                            {senet.senetBaslangicTarihi ?
                                                                new Date(senet.senetBaslangicTarihi).toLocaleDateString('tr-TR') :
                                                                'Tarih yok'
                                                            }
                                                        </small>
                                                    </div>
                                                </td>
                                                <td>
                                                    <div>
                                                        <strong>{senet.customerData?.name}</strong>
                                                        <br />
                                                        <small className="text-muted">{senet.customerData?.phone}</small>
                                                        <br />
                                                        <small className="text-muted">{senet.customerData?.tcNo}</small>
                                                    </div>
                                                </td>
                                                <td>
                                                    <Badge bg={senet.identityType === 'VKN' ? 'info' : 'secondary'}>
                                                        {senet.identityType === 'VKN' ? 'Kurumsal' : 'Bireysel'}
                                                    </Badge>
                                                </td>
                                                <td>
                                                    <div>
                                                        <strong>{senet.vehicleData?.make} {senet.vehicleData?.model}</strong>
                                                        <br />
                                                        <small className="text-muted">{senet.vehicleData?.plate}</small>
                                                    </div>
                                                </td>
                                                <td>
                                                    <div>
                                                        <strong>{formatCurrency(senet.vehicleData?.price || 0)}</strong>
                                                        <br />
                                                        <small className="text-muted">
                                                            Peşin: {formatCurrency(senet.financialData?.downPayment || 0)}
                                                        </small>
                                                    </div>
                                                </td>
                                                <td>
                                                    <div>
                                                        <strong>{senet.financialData?.term} ay</strong>
                                                        <br />
                                                        <small className="text-muted">
                                                            {formatCurrency(senet.financialData?.monthlyPayment || 0)} / ay
                                                        </small>
                                                    </div>
                                                </td>
                                                <td>
                                                    {getStatusBadge(senet.paymentPlan)}
                                                    <br />
                                                    <small className="text-muted">
                                                        {paymentStatus.paid}/{paymentStatus.total}
                                                    </small>
                                                </td>
                                                <td>
                                                    <ButtonGroup size="sm">
                                                        <Button
                                                            variant="info"
                                                            onClick={() => handleShowDetails(senet._id)}
                                                            title="Detayları Görüntüle"
                                                        >
                                                            <FaEye />
                                                        </Button>

                                                        <Button
                                                            variant="success"
                                                            onClick={() => handleShowSenet(senet._id)}
                                                            title="Senet Görüntüle"
                                                        >
                                                            <FaFileContract />
                                                        </Button>

                                                        <Button
                                                            variant="danger"
                                                            onClick={() => handleShowDeleteModal(senet)}
                                                            title="Senet Sil"
                                                        >
                                                            <FaTrash />
                                                        </Button>
                                                    </ButtonGroup>
                                                </td>
                                            </tr>
                                        );
                                    })
                                ) : (
                                    <tr>
                                        <td colSpan="8" className="text-center py-4">
                                            {loading ? 'Yükleniyor...' : 'Henüz sözleşmesiz senet oluşturulmamış'}
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </Table>
                    </div>

                    {/* Pagination */}
                    {renderPagination()}
                </Card.Body>
            </Card>
            {/* Detail Modal */}
            <Modal show={showDetailModal} onHide={() => setShowDetailModal(false)} size="xl" fullscreen="lg-down">
                <Modal.Header closeButton>
                    <Modal.Title>Senet Detayları</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedSenet && (
                        <>
                            <Row>
                                <Col md={6}>
                                    <h5>Müşteri Bilgileri</h5>
                                    <p><strong>Ad Soyad:</strong> {selectedSenet.customerData?.name}</p>
                                    <p><strong>Telefon:</strong> {selectedSenet.customerData?.phone}</p>
                                    <p><strong>Email:</strong> {selectedSenet.customerData?.email || '-'}</p>
                                    <p><strong>Kimlik Türü:</strong> {selectedSenet.identityType === 'VKN' ? 'Kurumsal (VKN)' : 'Bireysel (TCKN)'}</p>
                                    <p><strong>Kimlik No:</strong> {selectedSenet.customerData?.tcNo}</p>
                                    <p><strong>Adres:</strong> {selectedSenet.customerData?.address || '-'}</p>

                                    {selectedSenet.guarantorData && (
                                        <>
                                            <h5 className="mt-4">Kefil Bilgileri</h5>
                                            <p><strong>Kefil Adı:</strong> {selectedSenet.guarantorData.name}</p>
                                            <p><strong>Kefil Kimlik:</strong> {selectedSenet.guarantorData.tcNo}</p>
                                            <p><strong>Kefil Adresi:</strong> {selectedSenet.guarantorData.address}</p>
                                        </>
                                    )}
                                </Col>
                                <Col md={6}>
                                    <h5>Araç & Finansal Bilgiler</h5>
                                    <p><strong>Araç:</strong> {selectedSenet.vehicleData?.make} {selectedSenet.vehicleData?.model}</p>
                                    <p><strong>Plaka:</strong> {selectedSenet.vehicleData?.plate}</p>
                                    <p><strong>Araç Bedeli:</strong> {formatCurrency(selectedSenet.vehicleData?.price || 0)}</p>
                                    <p><strong>Peşinat:</strong> {formatCurrency(selectedSenet.financialData?.downPayment || 0)}</p>
                                    <p><strong>Kredi Tutarı:</strong> {formatCurrency(selectedSenet.financialData?.loanAmount || 0)}</p>
                                    <p><strong>Vade:</strong> {selectedSenet.financialData?.term} ay</p>
                                    <p><strong>Faiz Oranı:</strong> %{selectedSenet.financialData?.interestRate}</p>
                                    <p><strong>Aylık Taksit:</strong> {formatCurrency(selectedSenet.financialData?.monthlyPayment || 0)}</p>
                                    <p><strong>Senet Başlangıç:</strong> {selectedSenet.senetBaslangicTarihi ? new Date(selectedSenet.senetBaslangicTarihi).toLocaleDateString('tr-TR') : '-'}</p>
                                </Col>
                            </Row>

                            {/* Ödeme Durumu - Tam Genişlik */}
                            <Row className="mt-4">
                                <Col md={12}>
                                    <h5 className="mb-3">📋 Ödeme Durumu</h5>
                                    {selectedSenet.paymentPlan && selectedSenet.paymentPlan.length > 0 ? (
                                        <div className="table-responsive">
                                            <Table striped bordered hover>
                                                <thead className="table-dark">
                                                    <tr>
                                                        <th>Taksit No</th>
                                                        <th>Vade Tarihi</th>
                                                        <th>Taksit Tutarı</th>
                                                        <th>Durum</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {selectedSenet.paymentPlan.map((item, index) => (
                                                        <tr key={index}>
                                                            <td className="text-center">
                                                                <Badge bg="primary">{item.taksit}/{selectedSenet.paymentPlan.length}</Badge>
                                                            </td>
                                                            <td>{new Date(item.taksitTarihi).toLocaleDateString('tr-TR')}</td>
                                                            <td className="text-end">
                                                                <strong>{formatCurrency(item.taksitTutari)}</strong>
                                                            </td>
                                                            <td className="text-center">
                                                                <Badge bg={item.paymentStatus === 'paid' ? 'success' : 'warning'}>
                                                                    {item.paymentStatus === 'paid' ? 'Ödendi' : 'Bekliyor'}
                                                                </Badge>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </Table>

                                            {/* Özet Bilgi */}
                                            <Alert variant="info" className="mt-3">
                                                <div className="d-flex justify-content-between">
                                                    <span><strong>Toplam Taksit:</strong> {selectedSenet.paymentPlan.length} adet</span>
                                                    <span><strong>Toplam Tutar:</strong> {formatCurrency(selectedSenet.paymentPlan.reduce((sum, item) => sum + (item.taksitTutari || 0), 0))}</span>
                                                </div>
                                            </Alert>
                                        </div>
                                    ) : (
                                        <Alert variant="warning">Ödeme planı bulunamadı</Alert>
                                    )}
                                </Col>
                            </Row>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowDetailModal(false)}>
                        Kapat
                    </Button>
                    <Button
                        variant="success"
                        onClick={() => handleShowSenet(selectedSenet?._id)}
                        disabled={!selectedSenet}
                    >
                        <FaFileContract className="me-2" />
                        Senet Görüntüle
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Silme Onay Modal */}
            <DeleteConfirmModal
                show={showDeleteModal}
                onHide={() => setShowDeleteModal(false)}
                report={selectedSenet}
                onDelete={handleDeleteSenet}
            />
         
        </Container>
    );
};

export default StandaloneSenetListPage;